import React from 'react';

import { LookoButton, LookoModalV2, VStack } from '@looko-design-system';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useBoolean } from 'usehooks-ts';

import { hasIntersection, when } from '@looko-corp/acloset-react-kit';

import { ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP } from '@/MetadataType';
import { useShopInfo2 } from '@/app/shop/shop-info/hooks';
import usePatchShop from '@/app/shop/usePatchShop';
import { useGlobalEventAlertDialog } from '@/components/GlobalEventAlertDialog';
import useUserState from '@/hook/useUserState';
import { roleManager, roleViewer } from '@/router/consts';

export const AI_상품정보_자동입력_동의_모달 = React.memo(function AI_상품정보_자동입력_동의_모달() {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useGlobalEventAlertDialog();

  const { shopInfo } = useShopInfo2();
  const { mutateAsync: patchShop, isPending } = usePatchShop();

  const user = useUserState();

  const 마스터_계정인가 = hasIntersection(user?.roles ?? [], [roleViewer, roleManager]);

  const 불러오기를_사용하는가 =
    when(
      {
        [ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP.cafe24]: shopInfo.cafe24.cafe24ItemLoadSetting?.enable,
        [ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP['naver-smart-store']]:
          shopInfo.naverSmartStore.naverItemLoadSetting?.enable,
        [ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP.bunjang]: shopInfo.bunjang.bunjangItemLoadSetting?.enable,
      },
      shopInfo.loadPlatformType ?? '',
    ) ?? false;

  const { value: isModalOpen, setFalse: closeModal } = useBoolean(
    dayjs(shopInfo.createDate).isBefore(dayjs('2024-11-08')) &&
      불러오기를_사용하는가 &&
      shopInfo.enableAutoInput == null,
  );

  const 자동분석_이용하지_않기_핸들러 = async () => {
    await patchShop({
      shopID: shopInfo.shopID,
      body: {
        enableAutoInput: false,
      },
    });

    openDialog({
      title: '설정이 반영되었습니다.\n기존 상품의 AI 추출정보는 그대로 유지됩니다.',
      cancelButtonText: undefined,
      content: (
        <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
          기존에 올린 상품에 오입력된 정보가 있더라도, 이에 따른 문제의 책임은 판매자에게 있습니다.{' '}
          <Typography variant="Body_Looko" color={'error.main'}>
            <b>
              입력된 상품 정보를 확인하여, 잘못된 정보를 수정하거나, 판매 진행중인 모든 상품을 판매 중지 처리해주세요.
            </b>
          </Typography>
          {'\n\n이 설정은 언제든지 설정>원클릭 판매확장 설정에서 변경할 수 있습니다.'}
        </Typography>
      ),
    });
    closeModal();
  };

  const 자동분석_동의하고_이용_핸들러 = async () => {
    await patchShop({
      shopID: shopInfo.shopID,
      body: {
        enableAutoInput: true,
      },
    });

    enqueueSnackbar('설정이 반영되었습니다', { variant: 'success' });
    closeModal();
  };

  return (
    <LookoModalV2 open={isModalOpen}>
      <LookoModalV2.Header>내 쇼핑몰에서 불러온 상품에 대한 AI 자동분석 서비스 이용 여부 확인</LookoModalV2.Header>

      <LookoModalV2.Content>
        <VStack width={560}>
          <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
            {`안녕하세요. 룩코팀입니다.
          
          AI 상품정보 자동입력에 관련하여 동의사항이 있어 공지드립니다.
          
          잘 아시다시피, Looko AI는 사진을 분석하여 상품 정보를 자동으로 입력합니다. 상세한 정보는 상품 노출과 판매에 중요한 영향을 미치는 중요한 요소이지만, 하나하나 직접 입력하기는 너무 번거롭기 때문입니다. 지난 기간동안 저희 AI 기술을 꾸준히 개선해왔고, 2024년 11월 기준 약 95%의 정확도를 보이고 있습니다.(사진의 품질과 배경유무 등에 따라 달라질 수 있습니다.)
          
          최근, AI가 잘못 분석한 정보가 세컨솔드(에이클로젯)에 노출되는 경우가 있었고, 구매자가 잘못된 정보를 사유로 판매자 귀책으로 반품을 신청하는 사례가 발생하였습니다. 이 문제의 1차적 원인은 저희 AI의 오류에 있음을 저희도 명확하게 인지하고 있습니다. 다만, 정보 오류로 인한 고객과의 분쟁 문제 (반품 배송비 배상 등)에 대해서 저희 회사가 모든 책임을 지는 것은 현실적으로 어려운 상황입니다.\n\n`}
          </Typography>

          <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
            이에 판매자별로{' '}
            <Typography color="error.main" variant="Body_Looko">
              <b>내 쇼핑몰에서 자동으로 불러온 상품</b>
            </Typography>
            에 대한, AI 자동분석 여부에 대한 동의를 받고자 합니다. 이용에 동의하지 않으시는 경우, 향후 자동으로 불러온
            모든 상품에 대해서는 AI 분석을 수행하지 않으며, 이에 따라 추가적인 정보는 세컨솔드에 기재되지 않을
            예정입니다. 이에 따라 고객들이 상품을 검색할 때, 상품들의 노출량이 감소할 수 있습니다.{'\n\n'}
          </Typography>

          <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
            AI 자동분석을 이용하시는 경우, 현재와 같이 브랜드, 소재, 원산지, 컬러, 카테고리, 사이즈 등 풍부한 정보를
            추출하여 입력해드릴 예정입니다. 다만 이 경우,{' '}
            <b>{"‘AI가 입력한 정보 오류에 따른 문제의 책임은 판매자에게 있음'"}에 동의</b>하는 것으로 간주됩니다.
            {'\n\n'}
          </Typography>

          <Typography variant="Body_Looko" whiteSpace={'pre-line'}>
            {`저희가 추천드리는 방향성은, AI 자동분석 서비스를 활용하여 노출량을 늘리시되, “상품 정보가 간혹 틀릴 수 있으니 반드시 상품 사진을 우선하여 참고해주세요” 와 같은 안내멘트를 상세페이지에 삽입하는 것입니다. 이렇게 되면, 구매자와의 정보 오류에 따른 분쟁 이슈도 감소하지 않을까 예상하고 있습니다. 다만, 이에 대한 판단은 전적으로 판매자에게 있습니다.
          
          Looko-AI 를 통해서 직접 업로드 과정을 수행하시는 분들의 경우, 이전과 동일하게 AI가 상품 정보를 분석하고, 그 분석 결과가 자동으로 입력되며, 위 내용은 내 쇼핑몰에서 자동으로 불러와서 세컨솔드 (에이클로젯)에 게시하는 경우에만 해당됩니다.
          
          이 설정은 언제든지 설정 원클릭 판매확장 설정에서 변경할 수 있습니다.`}
          </Typography>
        </VStack>
      </LookoModalV2.Content>

      <LookoModalV2.Actions>
        <LookoButton variant="outlined" size="large" loading={isPending} onClick={자동분석_이용하지_않기_핸들러}>
          자동 분석 이용하지 않기
        </LookoButton>
        <LookoButton
          variant="contained"
          size="large"
          widthSize="small"
          loading={isPending}
          onClick={자동분석_동의하고_이용_핸들러}
        >
          동의하고 이용
        </LookoButton>
        {마스터_계정인가 && (
          <LookoButton variant="outlined" size="large" color="inherit" onClick={closeModal}>
            마스터계정 전용 닫기
          </LookoButton>
        )}
      </LookoModalV2.Actions>
    </LookoModalV2>
  );
});
